import { useEffect, useState } from 'react';
import { useCombobox } from '@mantine/core';
import { useDebounceValue } from 'usehooks-ts';

type ComboboxSearchResult<T> = {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  results: T[];
  setResults: (value: T[]) => void;
  isLoading: boolean;
  isEmpty: boolean;
  combobox: ReturnType<typeof useCombobox>;
  handleSearch: (value: string) => void;
};

type UseComboboxSearchOptions<TRaw, TMapped> = {
  fetchFn: (query: string) => Promise<TRaw[]>;
  mapFn: (item: TRaw) => TMapped;
  minChars?: number;
  debounceMs?: number;
};

export function useComboboxSearch<TRaw, TMapped>({
  fetchFn,
  mapFn,
  minChars = 3,
  debounceMs = 500,
}: UseComboboxSearchOptions<TRaw, TMapped>): ComboboxSearchResult<TMapped> {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, debounceMs);
  const [results, setResults] = useState<TMapped[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  useEffect(() => {
    if (debouncedSearchTerm.length < minChars) {
      setResults([]);
      setIsEmpty(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchFn(debouncedSearchTerm)
      .then((raw) => {
        const mapped = raw.map(mapFn);
        setResults(mapped);
        setIsEmpty(mapped.length === 0);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [debouncedSearchTerm]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    combobox.resetSelectedOption();
    combobox.openDropdown();
  };

  return {
    searchTerm,
    setSearchTerm,
    results,
    setResults,
    isLoading,
    isEmpty,
    combobox,
    handleSearch,
  };
}
